import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DotsMoreIcon } from 'assets/icons';
import Popover from 'components/Popover';
import PrivateComponent from 'composers/PrivateComponent';
import {
  ChangeVehiclePopup,
  SearchHistoryPopoverTitle,
  SHistoryTitleText,
} from 'pages/CatalogPage/common/CatalogBanner/CatalogVehicleTab';
import SearchHistoryContent from 'pages/CatalogPage/DH/PopoverContent';
import { theme } from 'styles';
import { IconButton } from 'UI';
import { Country } from 'utils/i18n/Country';
import {
  SMenuContainer,
  SMenuDropdown,
  SMenuHeader,
  SMenuItem,
  SMenuTrigger,
} from './CatalogVehicleOptionsMenu.styled';

const FR_INSPECTION_CHECKLIST_URL =
  'https://s3.eu-west-1.amazonaws.com/provider.medias.parts-io.com/docs/pdfs/TECHNICALCONTROL_FR_20250317.pdf';

type ActiveOverlay = 'changeVehicle' | 'searchHistory' | null;

export const CatalogVehicleOptionsMenu = ({
  query,
  setIsChangeVehicle,
  isCompact = false,
}: {
  query: string;
  setIsChangeVehicle?: (b: boolean) => void;
  isCompact?: boolean;
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeOverlay, setActiveOverlay] = useState<ActiveOverlay>(null);

  useEffect(() => {
    setIsMenuOpen(false);
    setActiveOverlay(null);
  }, [query]);

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (isMenuOpen && event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen]);

  const closeOverlay = () => setActiveOverlay(null);

  const onMenuContainerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const onOptionsButtonClick = () => {
    setIsMenuOpen((open) => !open);
  };

  const onChangeVehicleClick = () => {
    setIsMenuOpen(false);
    setActiveOverlay('changeVehicle');
  };

  const onInspectionChecklistClick = () => {
    window.open(FR_INSPECTION_CHECKLIST_URL, '_blank', 'noopener,noreferrer');
    setIsMenuOpen(false);
  };

  const onSearchHistoryClick = () => {
    setIsMenuOpen(false);
    setActiveOverlay('searchHistory');
  };

  const getPopoverContent = () => {
    if (activeOverlay === 'changeVehicle') {
      return (
        <ChangeVehiclePopup
          visible
          setIsChangeVehiclePopupVisible={(visible) => {
            if (!visible) {
              closeOverlay();
            }
          }}
          setIsChangeVehicle={setIsChangeVehicle}
        />
      );
    }
    if (activeOverlay === 'searchHistory') {
      return <SearchHistoryContent />;
    }
    return null;
  };

  const getPopoverTitle = () => {
    if (activeOverlay === 'changeVehicle') {
      return <SHistoryTitleText>{t('catalog.parts.action.change_vehicle', 'Change vehicle')}</SHistoryTitleText>;
    }
    if (activeOverlay === 'searchHistory') {
      return <SearchHistoryPopoverTitle />;
    }
    return undefined;
  };

  const popoverContent = getPopoverContent();
  const popoverTitle = getPopoverTitle();

  return (
    <SMenuContainer ref={menuRef} onClick={onMenuContainerClick} data-cy={'vehicle-options-menu'}>
      <SMenuTrigger
        type={'button'}
        aria-label={t('common.options', 'Options')}
        aria-expanded={isMenuOpen}
        onClick={onOptionsButtonClick}
        data-cy={'vehicle-options-menu-trigger'}
      >
        <IconButton
          IconComponent={DotsMoreIcon}
          round
          size={isCompact ? 10 : 22}
          boxSize={isCompact ? 'xsm' : 'lg'}
          color={'grey15'}
          bgColor={'grey95'}
          hoverFill={'grey75'}
          noPointer
        />
      </SMenuTrigger>
      {isMenuOpen && activeOverlay === null && (
        <SMenuDropdown role={'menu'}>
          <SMenuHeader>{t('common.options', 'Options')}</SMenuHeader>
          <SMenuItem type={'button'} role={'menuitem'} onClick={onChangeVehicleClick} data-cy={'change-vehicle-button'}>
            {t('catalog.parts.action.change_vehicle', 'Change vehicle')}
          </SMenuItem>
          <PrivateComponent
            requiredCountries={[Country.FR]}
            render={() => (
              <SMenuItem
                type={'button'}
                role={'menuitem'}
                onClick={onInspectionChecklistClick}
                data-cy={'inspection-checklist-button'}
              >
                {t('catalog.parts.action.inspection_checklist', 'Inspection Checklist')}
              </SMenuItem>
            )}
          />
          <SMenuItem type={'button'} role={'menuitem'} onClick={onSearchHistoryClick} data-cy={'search-history-button'}>
            {t('catalog.history', 'Search History')}
          </SMenuItem>
        </SMenuDropdown>
      )}
      <Popover
        id={`vehicle_options_overlay_${query}`}
        visible={activeOverlay !== null}
        onVisibleChange={(visible) => {
          if (!visible) {
            closeOverlay();
          }
        }}
        trigger={[]}
        title={popoverTitle}
        content={popoverContent ?? <></>}
        placement={'bottomRight'}
        arrowPointAtCenter={true}
        arrowColor={theme.color.brand_black}
      >
        <span aria-hidden={'true'} style={{ position: 'absolute', top: 0, right: 0, width: 1, height: 1 }} />
      </Popover>
    </SMenuContainer>
  );
};
